import { House, LogIn } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useEmailToken } from "../hooks/useEmailToken.js";

import { LinkAction, Screen } from "../components/Screen.js";
import { resendVerification, verifyEmail } from "../services/authService.js";

/**
 * The other end of the link in the verification email.
 *
 * # Why a page and not a `GET` route on the server
 *
 * `src/mail/templates.rs` points the link **here** rather than at the API, and
 * says why: a `GET` that verified an address directly would be triggered by any
 * mail client or corporate scanner that prefetches links — and prefetching is
 * exactly how a single-use token gets spent before its owner ever clicks it. So
 * the link opens a page, and the page posts.
 *
 * The post is guarded by a ref: React's StrictMode runs an effect twice in
 * development, and the second run would spend a token the first one already
 * used, showing "this link has already been used" to somebody who clicked once.
 */
export function VerifyPage() {
  const { t } = useTranslation();
  const token = useEmailToken();
  const [state, setState] = useState<"working" | "done" | "failed">("working");
  const [error, setError] = useState<string | null>(null);
  const spent = useRef(false);

  useEffect(() => {
    // A link with no token is decided by the URL, not by a request — so it is
    // rendered below rather than set here. An effect that calls setState
    // synchronously is a cascading render, and the lint says so.
    if (!token || spent.current) {
      return;
    }
    spent.current = true;

    void (async () => {
      try {
        await verifyEmail(token);
        setState("done");
      } catch (cause) {
        setState("failed");
        setError(cause instanceof Error ? cause.message : t("verify.failed"));
      }
    })();
  }, [token, t]);

  if (!token) {
    return (
      <Screen title={t("verify.incomplete.title")} lead={t("verify.incomplete.lead")}>
        <ResendBox />
      </Screen>
    );
  }

  if (state === "working") {
    return <Screen title={t("verify.working")}>{null}</Screen>;
  }

  if (state === "done") {
    return (
      <Screen
        title={t("verify.done.title")}
        lead={t("verify.done.lead")}
        footer={
          <LinkAction to="/" icon={House} accent>
            {t("verify.done.action")}
          </LinkAction>
        }
      >
        {null}
      </Screen>
    );
  }

  return (
    <Screen
      title={t("verify.dead.title")}
      lead={error ?? undefined}
      footer={
        <LinkAction to="/login" icon={LogIn}>
          {t("login.title")}
        </LinkAction>
      }
    >
      <ResendBox />
    </Screen>
  );
}

/** Another link, for a token that expired or was already spent. */
function ResendBox() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);

  if (sent) {
    return <p className="text-sm text-white/60">{t("verify.resend.sent")}</p>;
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        // Deliberately ignoring the outcome: this route answers identically
        // whether or not the address exists (ADR 0010), so there is nothing to
        // report and nothing worth reporting a failure about either.
        void resendVerification(email.trim()).finally(() => {
          setSent(true);
        });
      }}
      className="flex flex-col gap-3"
    >
      <label className="text-sm text-white/70" htmlFor="resend-email">
        {t("verify.resend.label")}
      </label>
      <input
        id="resend-email"
        type="email"
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
        }}
        autoComplete="email"
        autoCapitalize="none"
        required
        className="min-h-14 rounded-lg border border-white/15 bg-stage-900 px-4 text-lg outline-none focus:border-flash-500"
      />
      <button
        type="submit"
        disabled={!email.trim()}
        className="min-h-12 rounded-lg border border-white/20 px-4 font-medium disabled:opacity-40"
      >
        {t("verify.resend.action")}
      </button>
    </form>
  );
}
